/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import _ from "lodash";
import Plotly from "plotly.js";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Icon, Loader, Message, Table } from "semantic-ui-react";
import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import { getPrecision, getPrecisionString } from "../../../Lumber/ValuePrecisionConverter";
import DataTable, { DataTableZone } from "./DataTable";
import { getColorFromPalette } from "./MapStyles";
import {
	HeatmapData,
	PlotProps,
	useData,
	useDataDispatch,
	useGraph,
	useGraphDispatch,
	useViewerDispatch,
} from "./StateManagement";
import { DateTime } from "luxon";
import { SQFT_PER_ACRE } from "../../../../utilities/constants";
import { maxDecimalPlaces } from "../../../../utilities/fmt";
import styles from "./styles.module.css";

function formatCatValue(v: number | string, decimals: number): string {
	return typeof v === "number" ? v.toFixed(decimals) : v;
}

// Mean analysis value across the plots that actually have heatmap data, or null when none do.
// Shared by the bar traces and the accompanying data table so the two can't disagree.
function meanPlotValue(plots: PlotProps[], dataMap: _.Dictionary<HeatmapData>): number | null {
	const withData = plots.filter((p) => dataMap[p.plotId] !== undefined);
	return withData.length > 0 ? _.meanBy(withData, (p) => dataMap[p.plotId].plotAnalysisResultValue) : null;
}

export function getPlotDataParams(plotData: PlotProps[], heatmapData: HeatmapData[], hideExclusions: boolean) {
	const includedPlots = hideExclusions ? _.filter(plotData, (pd) => !pd.status?.includes("Excluded")) : plotData;
	const zonedPlots = _.filter(includedPlots, (pd) => !!pd.group);
	const filteredPlots = zonedPlots.length > 0 ? zonedPlots : includedPlots;
	const broadGroups = _.groupBy(filteredPlots, "treatment");
	const treatmentMap = _.chain(filteredPlots)
		.groupBy("treatment")
		.mapValues((p) => p[0].treatmentName)
		.value();
	const dataMap = _.keyBy(heatmapData, "plotId");
	const dataMapGrouped = _.groupBy(heatmapData, "plotId");
	return { broadGroups, treatmentMap, dataMap, dataMapGrouped };
}

type TracesPlotData = Partial<Plotly.PlotData>[];
type TracesBoxPlotData = Partial<Plotly.BoxPlotData>[];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TracesTableData = Partial<any>[]; // Plotly doesn't have types for table data yet
type Layout = Partial<Plotly.Layout>;

// Plot layout mirrored by the accompanying DataTable so each of its columns sits over a box.
const CHART_MARGIN_LEFT = 75;
const CHART_MARGIN_RIGHT = 15;
const CHART_BAR_GAP = 0.2;
const CHART_BOX_GAP = 0.3;
const CHART_MAX_HEIGHT = 500;
const CHART_MARGIN_VERTICAL = 90;

const Y_TITLE_FONT_MAX = 20;
const Y_TITLE_FONT_MIN = 11;
const Y_TITLE_CHAR_WIDTH = 0.55;

function fitYAxisTitleSize(text: string, availableHeight: number): number {
	if (availableHeight <= 0 || text.length === 0) {
		return Y_TITLE_FONT_MAX;
	}

	const fitted = availableHeight / (text.length * Y_TITLE_CHAR_WIDTH);
	return Math.round(_.clamp(fitted, Y_TITLE_FONT_MIN, Y_TITLE_FONT_MAX));
}

type Props = { resizing: boolean; graphStyle?: React.CSSProperties };

const Graph = ({ resizing, graphStyle }: Props) => {
	const { updateSelectedPlotIds, updateLegendStatus } = useViewerDispatch();
	const {
		heatmapData,
		heatmapDataAllDates,
		heatmapDataLoading,
		plotData,
		dataLayerNames,
		dataLayerValues,
		hideExclusions,
		showCategoricalValues,
	} = useData();
	const { updateHideExclusions, updateShowCategoricalValues } = useDataDispatch();
	const { xAxis, yAxis, xOrder, chartType, showDataTable } = useGraph();
	const { updateShowDataTable } = useGraphDispatch();
	const [legendStatus, setLegendStatus] = useState<{ [key: string]: boolean | "legendonly" }>({
		unassigned: "legendonly",
	});
	const [isSelecting, setIsSelecting] = useState(false);
	const [dragMode, setDragMode] = useState<Layout["dragmode"]>("pan");
	const [selections, setSelections] = useState<unknown[] | undefined>(undefined);
	const [shapes, setShapes] = useState<Layout["shapes"]>(undefined);

	// Declared up here because boxPlotLayout sizes the y-axis title against the height the table leaves behind.
	const dataTableRef = useRef<HTMLDivElement>(null);
	const [dataTableHeight, setDataTableHeight] = useState(0);

	// Treatment set the legend defaults were last applied for; changes when the experimental design is switched.
	const legendTreatmentsKey = useRef<string | null>(null);

	// The toggle is only meaningful when an experimental design with zone assignments exists.
	const hasExperimentalDesigns = (plotData?.some((p) => p.group) ?? false) && dataLayerNames.length > 0;

	// The accompanying data table is only offered alongside the bar and box-whisker charts.
	const supportsDataTable = chartType === "bar" || chartType === "box" || chartType === "box-jitter";

	const effectiveXOrder: string[] = xOrder.length > 0 ? xOrder : dataLayerNames;

	// Keyed on the chart's identity: switching chart type or either axis is when a reset is correct.
	const uiRevision = `${chartType}|${xAxis}|${yAxis}`;

	// Derive a uniform decimal precision from the numeric zone values so that e.g.
	// [5, 5.5, 6] all render with one decimal place (recovers trailing zeros lost at JSON parse).
	const catValueDecimals = maxDecimalPlaces(dataLayerValues.filter((v): v is number => typeof v === "number"));

	const tableColumnHeaders: string[] =
		showCategoricalValues && dataLayerValues.length > 0
			? dataLayerNames.map((name, i) => `${name} (${formatCatValue(dataLayerValues[i], catValueDecimals)})`)
			: dataLayerNames;

	useEffect(() => {
		updateLegendStatus(legendStatus);
	}, [legendStatus, updateLegendStatus]);

	const buildBoxPlotData = useCallback(
		(
			dataMap: _.Dictionary<HeatmapData>,
			broadGroups: _.Dictionary<PlotProps[]>,
			treatmentMap: { [key: string]: string },
			legendStatus: { [key: string]: boolean | "legendonly" },
			jitter: boolean = true,
		) => {
			const compoundGroupedData: {
				[key: string]: { y: number[]; x: string[]; customdata: string[][] };
			} = {};

			_.forOwn(broadGroups, (plots, broadGroup) => {
				compoundGroupedData[broadGroup] = { y: [], x: [], customdata: [] };
				_.forEach(plots, (plot) => {
					if (dataMap[plot.plotId]) {
						compoundGroupedData[broadGroup].y.push(dataMap[plot.plotId].plotAnalysisResultValue);
						compoundGroupedData[broadGroup].x.push(String(dataLayerNames[plot.group ?? 0] ?? "None"));
						compoundGroupedData[broadGroup].customdata.push([plot.plotId, plot.treatment.toString()]);
					}
				});
			});

			const traces: TracesBoxPlotData = [];
			let i = 0;
			_.forOwn(compoundGroupedData, (traceData, group) => {
				if (group !== "") {
					traces.push({
						y: traceData.y,
						x: traceData.x,
						customdata: traceData.customdata,
						boxpoints: jitter ? "all" : "outliers",
						jitter: jitter ? 0.5 : 0,
						pointpos: 0,
						type: "box",
						marker: {
							color: getColorFromPalette(Number(group) - 1),
						},
						line: {
							dash: i < 20 ? "solid" : "dash",
						},
						name: treatmentMap[group],
						visible: legendStatus[treatmentMap[group]],
					});
					i++;
				}
			});

			return traces;
		},
		[dataLayerNames],
	);

	const buildBarChartData = useCallback(
		(
			dataMap: _.Dictionary<HeatmapData>,
			broadGroups: _.Dictionary<PlotProps[]>,
			treatmentMap: { [key: string]: string },
			legendStatus: { [key: string]: boolean | "legendonly" },
		) => {
			const compoundGroupedData: {
				[key: string]: { y: number[]; x: string[]; customdata: string[] };
			} = {};

			_.forOwn(broadGroups, (plots, broadGroup) => {
				compoundGroupedData[broadGroup] = { y: [], x: [], customdata: [] };
				const averages = _.chain(plots)
					.filter((p) => dataMap[p.plotId] !== undefined)
					.groupBy((p) => dataLayerNames[p.group ?? 0])
					.mapValues((zonePlots) => meanPlotValue(zonePlots, dataMap))
					.mapKeys((_, g) => (g === "undefined" ? "None" : g))
					.entries()
					.value();
				const averagesTransposed = _.zip(...averages);
				compoundGroupedData[broadGroup] = {
					y: averagesTransposed[1] as number[],
					x: averagesTransposed[0] as string[],
					customdata: Array<string>(averagesTransposed[0].length).fill(broadGroup),
				};
			});

			const traces: TracesPlotData = [];
			_.forOwn(compoundGroupedData, (traceData, group) => {
				if (group !== "") {
					traces.push({
						y: traceData.y,
						x: traceData.x,
						customdata: traceData.customdata,
						type: "bar",

						marker: {
							color: getColorFromPalette(Number(group) - 1) + "80",
							line: {
								color: getColorFromPalette(Number(group) - 1),
								width: 2,
							},
						},
						name: treatmentMap[group],
						visible: legendStatus[treatmentMap[group]],
					});
				}
			});

			return traces;
		},
		[dataLayerNames],
	);

	const buildTableData = useCallback(
		(
			dataMap: _.Dictionary<HeatmapData>,
			broadGroups: _.Dictionary<PlotProps[]>,
			treatmentMap: { [key: string]: string },
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			_legendStatus: { [key: string]: boolean | "legendonly" },
		) => {
			// Cells carry a stable UUID generated once at build time so React keys
			// stay invariant across re-renders even when zone reorderings cause two
			// cells in the same row to share a display value (e.g. multiple "-").
			const tableData: { id: string; value: string }[][] = [];
			const makeCell = (value: string) => ({ id: crypto.randomUUID(), value });

			const compound = _.mapValues(broadGroups, (p) => _.groupBy(p, "group"));
			const compoundAverages = _.mapValues(compound, (g) =>
				_.mapValues(g, (p) => [
					_.meanBy(
						_.filter(p, (plot) => dataMap[plot.plotId] !== undefined),
						(plot) => dataMap[plot.plotId].plotAnalysisResultValue,
					),
					p.length,
				]),
			);

			_.forOwn(compoundAverages, (groups, treatment) => {
				if (treatment) {
					const row: { id: string; value: string }[] = [makeCell(treatmentMap[treatment])];
					if (dataLayerNames.length === 0) {
						const average = groups[0];
						if (average !== undefined) {
							const rounded = getPrecision(average[0]) as number;
							row.push(makeCell(`${rounded} (n=${average[1]})`));
						} else {
							row.push(makeCell("-"));
						}
					}
					for (let i = 1; i < dataLayerNames.length; i++) {
						const average = groups[i];
						if (average !== undefined) {
							const rounded = getPrecision(average[0]) as number;
							row[i] = rounded ? makeCell(`${rounded} (n=${average[1]})`) : makeCell("-");
						} else {
							row[i] = makeCell("-");
						}
					}
					tableData.push(row);
				}
			});

			const traces: TracesTableData = [];
			traces.push({
				type: "table",
				header: {
					values: [
						dataLayerNames.length > 0 ? [undefined, { span: dataLayerNames.length - 1, title: xAxis }] : undefined,
						["Treatments"].concat(dataLayerNames.length === 0 ? "All Plots" : tableColumnHeaders.slice(1)),
					],
					font: { family: "Inter Tight", weight: 600, size: 20 },
					height: 30,
				},
				cells: {
					values: tableData,
					font: { family: "Inter Tight", size: 18 },
					// align: ["left"],
					height: 28,
				},
			});

			return traces;
		},
		[dataLayerNames, tableColumnHeaders, xAxis],
	);

	const buildLineChartData = useCallback(
		(
			dataMapGrouped: _.Dictionary<HeatmapData[]>,
			broadGroups: _.Dictionary<PlotProps[]>,
			treatmentMap: { [key: string]: string },
			legendStatus: { [key: string]: boolean | "legendonly" },
		) => {
			// Collect all zone indices present across all plots
			const zoneIndices = _.chain(broadGroups)
				.values()
				.flatten()
				.map((p) => p.group ?? 0)
				.uniq()
				.sortBy()
				.value();

			// Field plant date from the plot data (.NET DateTime.MinValue (0001-01-01) if no plant date exists)
			const plantDate = _.chain(broadGroups)
				.values()
				.flatten()
				.map((p) => DateTime.fromISO(p.plantDate))
				.find((d) => d.isValid && d.year > 1)
				.value();

			const traces: TracesPlotData = [];

			_.forOwn(broadGroups, (plots, broadGroup) => {
				if (broadGroup === "") return;

				const treatmentNum = Number(broadGroup);
				zoneIndices.forEach((zoneIdx, subplotIdx) => {
					const zonePlots = plots.filter((p) => (p.group ?? 0) === zoneIdx);
					const allEntries = _.flatMap(zonePlots, (p) => dataMapGrouped[p.plotId] ?? []);

					const dailyAverages = _.chain(allEntries)
						.groupBy("dateCollected")
						.mapValues((entries) => _.meanBy(entries, "plotAnalysisResultValue"))
						.mapKeys((_, g) => (g === "undefined" ? "None" : g))
						.entries()
						.value();

					if (!dailyAverages.length) return;

					// Mirrors the small plot timecourse: start the line at (plant date, 0) unless a flight falls on that day
					if (plantDate && !dailyAverages.some(([date]) => DateTime.fromISO(date).hasSame(plantDate, "day"))) {
						dailyAverages.push([plantDate.toISO()!, 0]);
					}

					const averages = _.sortBy(dailyAverages, ([date]) => DateTime.fromISO(date).toMillis());

					const averagesTransposed = _.zip(...averages);
					const axisNum = subplotIdx + 1;

					traces.push({
						y: averagesTransposed[1] as number[],
						x: averagesTransposed[0] as string[],
						customdata: Array<string>(averagesTransposed[0]?.length ?? 0).fill(broadGroup),
						type: "scatter",
						xaxis: axisNum === 1 ? "x" : `x${axisNum}`,
						yaxis: axisNum === 1 ? "y" : `y${axisNum}`,
						marker: {
							color: getColorFromPalette(treatmentNum - 1) + "80",
							line: {
								color: getColorFromPalette(treatmentNum - 1),
								width: 2,
							},
						},
						legendgroup: treatmentMap[broadGroup],
						showlegend: subplotIdx === 0,
						name: treatmentMap[broadGroup],
						visible: legendStatus[treatmentMap[broadGroup]],
					});
				});
			});

			return { traces, zoneIndices };
		},
		[],
	);

	const boxPlotLayout = useMemo<Layout>(
		() => ({
			boxmode: "group",
			margin: {
				b: 75,
				l: chartType === "table" ? 15 : CHART_MARGIN_LEFT,
				r: CHART_MARGIN_RIGHT,
				t: chartType === "table" ? 75 : 15,
			},
			autosize: true,
			paper_bgcolor: "#fff0",
			plot_bgcolor: "#fff0",
			dragmode: dragMode,
			uirevision: uiRevision,
			shapes,
			...(selections ? { selections } : {}),
			title:
				chartType === "table"
					? {
							text: yAxis,
							font: { family: "Inter Tight", weight: 600, size: 28 },
						}
					: undefined,
			yaxis: {
				title: {
					text: yAxis,
					font: {
						family: "Inter Tight",
						weight: 600,
						size: fitYAxisTitleSize(yAxis, CHART_MAX_HEIGHT - dataTableHeight - CHART_MARGIN_VERTICAL),
					},
				},
			},
			xaxis: {
				showticklabels: effectiveXOrder.length > 1 || effectiveXOrder[0] !== "None",
				automargin: true,
				title: { text: xAxis, font: { family: "Inter Tight", weight: 600, size: 20 }, standoff: 10 },
				tickfont: { family: "Inter Tight", size: 16 },
				categoryorder: "array",
				categoryarray: effectiveXOrder,
				...(showCategoricalValues && dataLayerValues.length > 0 && dataLayerNames.length > 0
					? {
							tickmode: "array" as const,
							tickvals: dataLayerNames,
							ticktext: dataLayerNames.map((_, i) => formatCatValue(dataLayerValues[i], catValueDecimals)),
						}
					: {}),
			},
			showlegend: false,
			bargap: CHART_BAR_GAP,
			boxgap: CHART_BOX_GAP,
			bargroupgap: 0.15,
		}),
		[
			yAxis,
			xAxis,
			effectiveXOrder,
			chartType,
			showCategoricalValues,
			dataLayerValues,
			dataLayerNames,
			catValueDecimals,
			dragMode,
			uiRevision,
			selections,
			shapes,
			dataTableHeight,
		],
	);

	// Re-applies legend defaults whenever the treatment set changes (e.g. switching experimental designs):
	// a lone treatment is always shown, otherwise "unassigned" starts hidden.
	useEffect(() => {
		if (!plotData?.length || !heatmapData?.length) return;
		const { treatmentMap } = getPlotDataParams(plotData, heatmapData, hideExclusions);
		const treatmentNames = _.sortBy(Object.values(treatmentMap));
		const key = JSON.stringify(treatmentNames);
		if (legendTreatmentsKey.current === key) return;
		legendTreatmentsKey.current = key;
		setLegendStatus(treatmentNames.length === 1 ? { [treatmentNames[0]]: true } : { unassigned: "legendonly" });
	}, [plotData, heatmapData, hideExclusions]);

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const graphData = useMemo(() => {
		if ((heatmapData || heatmapDataAllDates) && plotData?.length) {
			if (heatmapData?.length === 0 && heatmapDataAllDates?.length === 0) return [];

			const { broadGroups, treatmentMap, dataMap } = heatmapData?.length
				? getPlotDataParams(plotData, heatmapData, hideExclusions)
				: { broadGroups: {}, treatmentMap: {}, dataMap: {} };

			const {
				broadGroups: broadGroupsAllDates,
				treatmentMap: treatmentMapAllDates,
				dataMapGrouped: dataMapGroupedAllDates,
			} = heatmapDataAllDates?.length
				? getPlotDataParams(plotData, heatmapDataAllDates, hideExclusions)
				: { broadGroups: {}, treatmentMap: {}, dataMapGrouped: {} };

			switch (chartType) {
				case "box-jitter":
					return buildBoxPlotData(dataMap, broadGroups, treatmentMap, legendStatus);
				case "box":
					return buildBoxPlotData(dataMap, broadGroups, treatmentMap, legendStatus, false);
				case "bar":
					return buildBarChartData(dataMap, broadGroups, treatmentMap, legendStatus);
				case "line":
					return buildLineChartData(dataMapGroupedAllDates, broadGroupsAllDates, treatmentMapAllDates, legendStatus);
				case "table":
					return buildTableData(dataMap, broadGroups, treatmentMap, legendStatus);
				default:
					return [];
			}
			//-- Case where plotData finished loading, but heatmapData stayed empty
		} else if (plotData?.length) return [];
	}, [
		heatmapData,
		heatmapDataAllDates,
		plotData,
		chartType,
		hideExclusions,
		legendStatus,
		buildBoxPlotData,
		buildBarChartData,
		buildLineChartData,
		buildTableData,
	]);

	useEffect(() => {
		setIsSelecting(false);
		updateSelectedPlotIds([]);
	}, [updateSelectedPlotIds, graphData]);

	// For line chart, graphData is { traces, zoneIndices }; for others it's a flat array.
	const linePlotData = chartType === "line" && graphData && "traces" in graphData ? graphData.traces : null;
	const lineZoneIndices =
		chartType === "line" && graphData && "zoneIndices" in graphData ? graphData.zoneIndices : null;
	const flatGraphData = chartType !== "line" ? (graphData as TracesPlotData | undefined) : null;
	const isEmpty =
		graphData !== undefined &&
		(chartType === "line" ? (linePlotData?.length ?? 0) === 0 : (flatGraphData?.length ?? 0) === 0);

	const dataTableZones = useMemo<DataTableZone[]>(() => {
		if (!supportsDataTable || !plotData?.length || !heatmapData?.length) return [];

		const { broadGroups, treatmentMap, dataMap } = getPlotDataParams(plotData, heatmapData, hideExclusions);
		const treatments = Object.keys(broadGroups).filter(
			(t) => t !== "" && legendStatus[treatmentMap[t]] !== "legendonly",
		);

		const zoneKey = (plot: PlotProps) => String(dataLayerNames[plot.group ?? 0] ?? "None");
		const plotsByTreatmentZone = _.mapValues(_.pick(broadGroups, treatments), (plots) => _.groupBy(plots, zoneKey));

		const zonesWithData = new Set(_.flatMap(_.values(plotsByTreatmentZone), (byZone) => Object.keys(byZone)));

		return effectiveXOrder
			.filter((zone) => zonesWithData.has(zone))
			.map((zone) => ({
				id: zone,
				columns: treatments.map((treatment) => {
					const plots = plotsByTreatmentZone[treatment]?.[zone] ?? [];
					const average = meanPlotValue(plots, dataMap);
					// Summed in square feet and converted once, so the only rounding is on the acre value.
					const acres = _.sumBy(plots, (p) => p.plotColumnWidth * p.plotRangeLength) / SQFT_PER_ACRE;

					return {
						id: `${zone}-${treatment}`,
						acreage: acres.toFixed(2),
						plotCount: String(plots.length),
						average: average === null ? "-" : (getPrecisionString(average) as string),
						// Raw mean kept alongside the display string so DataTable can shade the average row.
						averageValue: average,
					};
				}),
			}));
	}, [supportsDataTable, plotData, heatmapData, hideExclusions, legendStatus, effectiveXOrder, dataLayerNames]);

	const showDataTableForChart = supportsDataTable && showDataTable && dataTableZones.length > 0;

	const dataTableGroupGap = chartType === "bar" ? CHART_BAR_GAP : CHART_BOX_GAP;

	useEffect(() => {
		const element = dataTableRef.current;
		if (!element) {
			setDataTableHeight(0);
			return;
		}
		const observer = new ResizeObserver(() => setDataTableHeight(element.offsetHeight));
		observer.observe(element);
		return () => observer.disconnect();
	}, [showDataTableForChart]);

	const lineChartLayout = useMemo<Layout>(() => {
		if (chartType !== "line" || !lineZoneIndices) return {};
		const n = lineZoneIndices.length || 1;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const layout: Record<string, any> = {
			autosize: true,
			paper_bgcolor: "#fff0",
			plot_bgcolor: "#fff0",
			dragmode: dragMode,
			uirevision: uiRevision,
			shapes,
			...(selections ? { selections } : {}),
			margin: { b: xAxis ? 90 : 60, l: 75, r: 15, t: 40 },
			showlegend: false,
			grid: { rows: 1, columns: n, pattern: "independent" },
			annotations:
				xAxis && xAxis !== "All Plots"
					? [
							{
								text: xAxis,
								font: { family: "Inter Tight", weight: 600, size: 16 },
								showarrow: false,
								xref: "paper",
								yref: "paper",
								x: 0.5,
								y: -0.2,
								xanchor: "center",
								yanchor: "top",
							},
						]
					: [],
		};

		lineZoneIndices.forEach((zoneIdx, i) => {
			const axisNum = i + 1;
			const xKey = axisNum === 1 ? "xaxis" : `xaxis${axisNum}`;
			const yKey = axisNum === 1 ? "yaxis" : `yaxis${axisNum}`;
			const zoneLabel =
				showCategoricalValues && dataLayerValues[zoneIdx] != null
					? formatCatValue(dataLayerValues[zoneIdx], catValueDecimals)
					: String(dataLayerNames[zoneIdx] ?? "All Zones");

			layout[xKey] = {
				title: {
					text: zoneLabel,
					font: { family: "Inter Tight", weight: 600, size: 16 },
				},
				tickfont: { family: "Inter Tight", size: 12 },
				automargin: true,
				showline: true,
				linecolor: "#000",
				linewidth: 1,
			};
			layout[yKey] = {
				title: i === 0 ? { text: yAxis, font: { family: "Inter Tight", weight: 600, size: 16 } } : undefined,
				tickfont: { family: "Inter Tight", size: 12 },
				showline: true,
				linecolor: "#000",
				linewidth: 1,
				// Match small plot timecourse: start the y-axis at 0 so the plant date point sits on the axis
				rangemode: "tozero",
				zeroline: false,
			};
		});

		return layout as Layout;
	}, [
		chartType,
		lineZoneIndices,
		dataLayerNames,
		yAxis,
		xAxis,
		showCategoricalValues,
		dataLayerValues,
		catValueDecimals,
		dragMode,
		uiRevision,
		selections,
		shapes,
	]);

	return useMemo(
		() =>
			heatmapDataLoading || graphData === undefined ? (
				<Loader active />
			) : (
				<>
					{isEmpty && (
						<Message className={styles.graphNoDataMessage} warning icon={<Icon className="warning" />}>
							<Message.Content>No heatmap data found for this trial.</Message.Content>
						</Message>
					)}
					{showDataTableForChart && (
						<div ref={dataTableRef} style={{ width: "100%", ...graphStyle }}>
							<DataTable
								zones={dataTableZones}
								groupGap={dataTableGroupGap}
								marginLeft={CHART_MARGIN_LEFT}
								marginRight={CHART_MARGIN_RIGHT}
							/>
						</div>
					)}
					<div
						style={{
							width: "100%",
							height:
								chartType === "table" ? "auto" : showDataTableForChart ? `calc(60% - ${dataTableHeight}px)` : "60%",
							maxHeight: `${CHART_MAX_HEIGHT - (showDataTableForChart ? dataTableHeight : 0)}px`,
							background: "#ffffff80",
							borderRadius: "8px",
							marginTop: "10px",
							padding: "4px",
							position: "relative",
							...graphStyle,
						}}
					>
						{graphData && !resizing ? (
							!isEmpty ? (
								chartType !== "table" ? (
									<AFPlot
										disableHoverStyle
										style={{ width: "100%", height: "100%" }}
										layout={chartType === "line" ? lineChartLayout : boxPlotLayout}
										data={chartType === "line" ? (linePlotData ?? []) : (flatGraphData ?? [])}
										config={{ responsive: true }}
										useResizeHandler
										onRelayout={(e) => {
											const re = e as Record<string, unknown>;
											if (typeof re.dragmode === "string") setDragMode(re.dragmode as Layout["dragmode"]);
											if ("selections" in re) setSelections(re.selections as unknown[] | undefined);
											if ("shapes" in re) setShapes(re.shapes as Layout["shapes"]);
										}}
										onDeselect={() => {
											setIsSelecting(false);
											setSelections(undefined);
											updateSelectedPlotIds([]);
										}}
										onSelecting={(e) => {
											setIsSelecting(true);
											if (e.points.length > 0) {
												const selectedPlotIds = _.map(e.points, (pt) => {
													if (!Array.isArray(pt.customdata)) return "";
													return pt.customdata[0] as string;
												});
												updateSelectedPlotIds(selectedPlotIds);
											}
										}}
										onSelected={(e) => {
											if (e.points.length > 0) {
												const selectedPlotIds = _.map(e.points, (pt) => {
													if (!Array.isArray(pt.customdata)) return "";
													return pt.customdata[0] as string;
												});
												updateSelectedPlotIds(selectedPlotIds);
											}
										}}
										onHover={(e) => {
											if (isSelecting) return;
											if (e.points.length === 1) {
												const point = e.points[0];
												const selectedX = point.x;
												if (chartType === "box-jitter") {
													// custom data is plotId for point based charts
													const groupedPlots = point.data.customdata.reduce<string[]>((acc, cd, i) => {
														if (!Array.isArray(cd)) return acc;
														const x = point.data.x[i];
														const plotId = cd[0] as string;
														if (x === selectedX) {
															acc.push(plotId);
														}
														return acc;
													}, []);
													updateSelectedPlotIds(groupedPlots);
												} else if (chartType === "line" && plotData) {
													// custom data is treatmentId
													const groupedPlots = plotData
														.filter(
															(pd) =>
																(!hideExclusions || !pd.status?.includes("Excluded")) &&
																pd.treatment.toString() === point.customdata,
														)
														.map((p) => p.plotId);
													updateSelectedPlotIds(groupedPlots);
												} else if (plotData) {
													// custom data is treatmentId for non point based charts
													const groupedPlots = plotData
														.filter(
															(pd) =>
																(!hideExclusions || !pd.status?.includes("Excluded")) &&
																String(dataLayerNames[pd.group ?? 0]) === selectedX &&
																pd.treatment.toString() === point.customdata,
														)
														.map((p) => p.plotId);
													updateSelectedPlotIds(groupedPlots);
												}
											} else {
												updateSelectedPlotIds([]);
											}
										}}
										onUnhover={() => {
											if (isSelecting) return;
											updateSelectedPlotIds([]);
										}}
									/>
								) : (
									<>
										<div
											style={{
												width: "100%",
												textAlign: "center",
												fontWeight: "600",
												fontSize: "28px",
												padding: "0.4em",
												lineHeight: "28px",
											}}
										>
											{typeof boxPlotLayout.title === "string"
												? boxPlotLayout.title
												: (boxPlotLayout.title?.text ?? "")}
										</div>
										<Table
											celled
											striped
											style={{
												margin: "0 8px 8px",
												width: "stretch",
											}}
										>
											{/*
											 * Keys in this Table branch are all derived from stable identifiers rather than array
											 * indices (defensive programming)
											 *
											 *   • Top header row: keyed on `v?.title` (the analysis name). Hard-coded 2-entry shape;
											 *     the undefined spacer entry yields a stable "header-undefined" key.
											 *   • Zone header row: keyed on the column-name string. Relies on zone names being unique
											 *     within a trial.
											 *   • Body rows: keyed on the treatment name (row[0].value).
											 *   • Cells: keyed on a UUID generated once in buildTableData. UUIDs survive zone
											 *     reorderings (hypothetical) even when two cells in the same row share a display
											 *     value (e.g. multiple "-" placeholders).
											 */}
											<Table.Header>
												{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
												{(flatGraphData?.[0] as any)?.header?.values?.[0] && (
													<Table.Row>
														{_.map(
															// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
															(flatGraphData?.[0] as any)?.header?.values?.[0],
															(v: { span: number; title: string } | undefined) => (
																<Table.HeaderCell textAlign="center" key={`header-${v?.title}`} colSpan={v?.span}>
																	{v?.title}
																</Table.HeaderCell>
															),
														)}
													</Table.Row>
												)}
												<Table.Row>
													{_.map(
														// eslint-disable-next-line @typescript-eslint/no-explicit-any
														(flatGraphData?.[0] as any)?.header?.values?.[1] as string[],
														(v: string, i: number) => (
															<Table.HeaderCell key={`header-${v}`} textAlign={i === 0 ? "right" : "center"}>
																{v}
															</Table.HeaderCell>
														),
													)}
												</Table.Row>
											</Table.Header>
											<Table.Body>
												{_.map(
													// eslint-disable-next-line @typescript-eslint/no-explicit-any
													(flatGraphData?.[0] as any)?.cells?.values as { id: string; value: string }[][],
													(row) => (
														<Table.Row key={`row-${row[0]?.value}`}>
															{_.map(row, (cell, j) => (
																<Table.Cell key={cell.id} textAlign={j === 0 ? "right" : "center"}>
																	{cell.value}
																</Table.Cell>
															))}
														</Table.Row>
													),
												)}
											</Table.Body>
										</Table>
									</>
								)
							) : (
								<></>
							)
						) : (
							<Loader active />
						)}
					</div>
					<div className={styles.graphFooter}>
						{chartType !== "table" && (
							<div className={styles.graphLegend}>
								{_.map(
									// For line chart, deduplicate traces to one legend entry per treatment
									chartType === "line" ? _.uniqBy(linePlotData ?? [], "legendgroup") : flatGraphData,
									(trace) => {
										const idx = (
											chartType === "box-jitter" || chartType === "box"
												? (trace.customdata as Plotly.Datum[][])?.[0]?.[1]
												: (trace.customdata as Plotly.Datum[][])?.[0]?.[0]
										) as number;
										return (
											<span
												key={trace.name as string}
												className={`legendItem${trace.visible === "legendonly" ? " disabled" : ""}`}
												onMouseOver={() => {
													if (isSelecting) return;
													if (plotData) {
														const plots = _.reduce<PlotProps, string[]>(
															plotData,
															(acc, curr) => {
																return curr.treatmentName === trace.name &&
																	(!hideExclusions || !curr.status?.includes("Excluded"))
																	? acc.concat(curr.plotId)
																	: acc;
															},
															[],
														);
														updateSelectedPlotIds(plots);
													}
												}}
												onMouseOut={() => {
													if (isSelecting) return;
													updateSelectedPlotIds([]);
												}}
												onClick={() => {
													const toggle = (trace.visible ?? true) === true ? "legendonly" : true;
													setLegendStatus((prev) => {
														const next = { ...prev, [trace.name!]: toggle as boolean | "legendonly" };
														return next;
													});
												}}
											>
												<div
													className={styles.legendSwatch}
													style={{
														border: `2.5px solid ${getColorFromPalette(idx - 1)}`,
														backgroundColor: `${getColorFromPalette(idx - 1)}80`,
													}}
												/>
												{idx} - {trace.name}
											</span>
										);
									},
								)}
							</div>
						)}
						<div className={styles.graphControls}>
							<Checkbox
								toggle
								defaultChecked
								onChange={(_, { checked }) => {
									updateHideExclusions(checked ?? true);
								}}
							/>
							<span className={styles.graphControlLabel}>Hide Exclusions</span>
							<Checkbox
								toggle
								disabled={!hasExperimentalDesigns}
								onChange={(_, { checked }) => {
									updateShowCategoricalValues(checked ?? false);
								}}
							/>
							<span className={styles.graphControlLabel}>Show Categorical Values</span>
							{supportsDataTable && (
								<>
									<Checkbox
										toggle
										checked={showDataTable}
										onChange={(_, { checked }) => {
											updateShowDataTable(checked ?? false);
										}}
									/>
									<span className={styles.graphControlLabel}>Show Data Table</span>
								</>
							)}
						</div>
					</div>
				</>
			),
		[
			graphData,
			flatGraphData,
			linePlotData,
			isEmpty,
			heatmapDataLoading,
			resizing,
			boxPlotLayout,
			lineChartLayout,
			chartType,
			updateSelectedPlotIds,
			isSelecting,
			plotData,
			dataLayerNames,
			hideExclusions,
			hasExperimentalDesigns,
			updateHideExclusions,
			updateShowCategoricalValues,
			supportsDataTable,
			showDataTable,
			updateShowDataTable,
			showDataTableForChart,
			dataTableZones,
			dataTableGroupGap,
			dataTableHeight,
			graphStyle,
		],
	);
};

export default Graph;
